import {
  clampNumber,
  normalizeViewMode,
  parseBooleanOr,
  parseFloatOr,
  parseIntegerOr,
  parseJsonOr,
  parseNullableFloat
} from "./shared"

const VIEWER_MODES = ["admin", "readonly", "embedded"]

const DEFAULT_VIEWER_CAPABILITIES_BY_MODE = {
  admin: {
    search: true,
    exportImage: true,
    liveDetails: true,
    mapping: true,
    viewerSettings: true,
    modelUpload: true,
    mutations: true,
    captureSessions: true
  },
  readonly: {
    search: true,
    exportImage: true,
    liveDetails: true,
    mapping: false,
    viewerSettings: false,
    modelUpload: false,
    mutations: false,
    captureSessions: true
  },
  embedded: {
    search: true,
    exportImage: false,
    liveDetails: true,
    mapping: false,
    viewerSettings: false,
    modelUpload: false,
    mutations: false,
    captureSessions: false
  }
}

export const viewerConfigFromDataset = (element) => parseJsonOr(element.dataset.viewerConfig, {})

export const viewerModeFromConfig = (config = {}) =>
  VIEWER_MODES.includes(config.mode) ? config.mode : "admin"

export const viewerCapabilitiesFromConfig = (config = {}) => {
  const mode = viewerModeFromConfig(config)
  const defaults = DEFAULT_VIEWER_CAPABILITIES_BY_MODE[mode] || DEFAULT_VIEWER_CAPABILITIES_BY_MODE.admin
  const capabilities = config.capabilities || parseJsonOr(config.viewerCapabilities, {})

  return {
    search: parseBooleanOr(capabilities.search, defaults.search),
    exportImage: parseBooleanOr(capabilities.export_image ?? capabilities.exportImage, defaults.exportImage),
    liveDetails: parseBooleanOr(capabilities.live_details ?? capabilities.liveDetails, defaults.liveDetails),
    mapping: parseBooleanOr(capabilities.mapping, defaults.mapping),
    viewerSettings: parseBooleanOr(capabilities.viewer_settings ?? capabilities.viewerSettings, defaults.viewerSettings),
    modelUpload: parseBooleanOr(capabilities.model_upload ?? capabilities.modelUpload, defaults.modelUpload),
    mutations: parseBooleanOr(capabilities.mutations, defaults.mutations),
    captureSessions: parseBooleanOr(capabilities.capture_sessions ?? capabilities.captureSessions, defaults.captureSessions)
  }
}

export const viewerSettingsFromDataset = (element) => ({
  objectIconScale: Math.max(parseFloatOr(element.dataset.viewerObjectIconScale, 1), 0.05),
  defaultViewMode: ["3d", "2d", "walkthrough"].includes(normalizeViewMode(element.dataset.viewerDefaultViewMode))
    ? normalizeViewMode(element.dataset.viewerDefaultViewMode)
    : "3d",
  defaultYawDegrees: parseFloatOr(element.dataset.viewerDefaultYawDegrees, 45),
  defaultPitchDegrees: parseFloatOr(element.dataset.viewerDefaultPitchDegrees, 21.5),
  defaultZoomMultiplier: Math.max(parseFloatOr(element.dataset.viewerDefaultZoomMultiplier, 1), 0.05),
  default2DZoomMultiplier: parseFloatOr(element.dataset.viewerDefault2dZoomMultiplier, 1),
  default2DOffsetX: parseFloatOr(element.dataset.viewerDefault2dOffsetX, 0),
  default2DOffsetZ: parseFloatOr(element.dataset.viewerDefault2dOffsetZ, 0),
  defaultVerticalBias: parseFloatOr(element.dataset.viewerDefaultVerticalBias, 0),
  walkthroughOffsetX: parseFloatOr(element.dataset.viewerWalkthroughOffsetX, 0),
  walkthroughOffsetZ: parseFloatOr(element.dataset.viewerWalkthroughOffsetZ, 0),
  walkthroughYawDegrees: parseFloatOr(element.dataset.viewerWalkthroughYawDegrees, 90),
  walkthroughPitchDegrees: parseFloatOr(element.dataset.viewerWalkthroughPitchDegrees, -2),
  walkthroughZoomMultiplier: Math.max(parseFloatOr(element.dataset.viewerWalkthroughZoomMultiplier, 1), 0.05),
  walkthroughEyeHeightRatio: Math.max(parseFloatOr(element.dataset.viewerWalkthroughEyeHeightRatio, 0.18), 0),
  walkthroughCameraX: parseNullableFloat(element.dataset.viewerWalkthroughCameraX),
  walkthroughCameraY: parseNullableFloat(element.dataset.viewerWalkthroughCameraY),
  walkthroughCameraZ: parseNullableFloat(element.dataset.viewerWalkthroughCameraZ),
  walkthroughTargetX: parseNullableFloat(element.dataset.viewerWalkthroughTargetX),
  walkthroughTargetY: parseNullableFloat(element.dataset.viewerWalkthroughTargetY),
  walkthroughTargetZ: parseNullableFloat(element.dataset.viewerWalkthroughTargetZ),
  modelOffsetY: parseFloatOr(element.dataset.viewerModelOffsetY, 0),
  labelsVisible: element.dataset.viewerLabelsVisible !== "false",
  snapToGround: parseBooleanOr(element.dataset.viewerSnapToGround, false)
})

export const viewerSettingsFromPayload = (payload = {}) => ({
  objectIconScale: payload.object_icon_scale,
  defaultViewMode: normalizeViewMode(payload.default_view_mode),
  defaultYawDegrees: payload.default_yaw_degrees,
  defaultPitchDegrees: payload.default_pitch_degrees,
  defaultZoomMultiplier: payload.default_zoom_multiplier,
  default2DZoomMultiplier: payload.default_2d_zoom_multiplier,
  default2DOffsetX: payload.default_2d_offset_x,
  default2DOffsetZ: payload.default_2d_offset_z,
  defaultVerticalBias: payload.default_vertical_bias,
  walkthroughOffsetX: payload.walkthrough_offset_x,
  walkthroughOffsetZ: payload.walkthrough_offset_z,
  walkthroughYawDegrees: payload.walkthrough_yaw_degrees,
  walkthroughPitchDegrees: payload.walkthrough_pitch_degrees,
  walkthroughZoomMultiplier: payload.walkthrough_zoom_multiplier,
  walkthroughEyeHeightRatio: payload.walkthrough_eye_height_ratio,
  walkthroughCameraX: payload.walkthrough_camera_x,
  walkthroughCameraY: payload.walkthrough_camera_y,
  walkthroughCameraZ: payload.walkthrough_camera_z,
  walkthroughTargetX: payload.walkthrough_target_x,
  walkthroughTargetY: payload.walkthrough_target_y,
  walkthroughTargetZ: payload.walkthrough_target_z,
  modelOffsetY: payload.model_offset_y,
  labelsVisible: payload.labels_visible,
  snapToGround: payload.snap_to_ground
})

const referenceLayerSettingsFromPayload = (payload = {}) => ({
  offsetX: payload.offset_x ?? payload.offsetX,
  offsetZ: payload.offset_z ?? payload.offsetZ,
  elevation: payload.elevation,
  rotationDegrees: payload.rotation_degrees ?? payload.rotationDegrees,
  scale: payload.scale,
  opacity: payload.opacity,
  whiteThreshold: payload.white_threshold ?? payload.whiteThreshold,
  whiteFeather: payload.white_feather ?? payload.whiteFeather
})

const normalizeCablingPlanSettings = (settings = {}) => ({
  offsetX: parseFloatOr(settings.offsetX, 0),
  offsetZ: parseFloatOr(settings.offsetZ, 0),
  elevation: parseFloatOr(settings.elevation, 0.02),
  rotationDegrees: parseFloatOr(settings.rotationDegrees, 0),
  scale: Math.max(parseFloatOr(settings.scale, 1), 0.01),
  opacity: clampNumber(parseFloatOr(settings.opacity, 0.88), 0, 1),
  whiteThreshold: clampNumber(parseIntegerOr(settings.whiteThreshold, 125), 0, 255),
  whiteFeather: clampNumber(parseIntegerOr(settings.whiteFeather, 18), 0, 255)
})

export const cloneCablingPlanSettings = (settings = {}) => normalizeCablingPlanSettings(settings)

export const normalizeReferenceLayer = (payload = {}) => ({
  id: payload.id == null ? null : String(payload.id),
  name: payload.name?.toString().trim() || "Reference Layer",
  position: parseIntegerOr(payload.position, 0),
  defaultVisible: parseBooleanOr(payload.default_visible ?? payload.defaultVisible, false),
  processed: parseBooleanOr(payload.processed, false),
  processingStatus: payload.processing_status?.toString() || payload.processingStatus?.toString() || "idle",
  processingError: payload.processing_error?.toString().trim() || payload.processingError?.toString().trim() || "",
  pdfUrl: payload.pdf_url?.toString() || payload.pdfUrl?.toString() || "",
  pdfFilename: payload.pdf_filename?.toString() || payload.pdfFilename?.toString() || "",
  imageUrl: payload.image_url?.toString() || payload.imageUrl?.toString() || "",
  imageFilename: payload.image_filename?.toString() || payload.imageFilename?.toString() || "",
  updateUrl: payload.update_url?.toString() || payload.updateUrl?.toString() || "",
  destroyUrl: payload.destroy_url?.toString() || payload.destroyUrl?.toString() || "",
  settings: cloneCablingPlanSettings(referenceLayerSettingsFromPayload(payload.settings || {}))
})

export const referenceLayersFromDataset = (element) =>
  parseJsonOr(element.dataset.referenceLayers, [])
    .map((payload) => normalizeReferenceLayer(payload))
    .sort((left, right) => left.position - right.position)

export const findReferenceLayerById = (referenceLayers, id) => {
  if (id == null) return null

  const normalizedId = String(id)
  return referenceLayers.find((referenceLayer) => String(referenceLayer.id) === normalizedId) || null
}

export const initialReferenceLayerId = (referenceLayers) => {
  const preferredVisible = referenceLayers.find((referenceLayer) => referenceLayer.processed && referenceLayer.defaultVisible)
  if (preferredVisible) return preferredVisible.id

  const firstProcessed = referenceLayers.find((referenceLayer) => referenceLayer.processed)
  if (firstProcessed) return firstProcessed.id

  return referenceLayers[0]?.id || null
}

export const cablingPlanSettingsEqual = (left, right) => {
  const normalizedLeft = cloneCablingPlanSettings(left)
  const normalizedRight = cloneCablingPlanSettings(right)

  return normalizedLeft.offsetX === normalizedRight.offsetX &&
    normalizedLeft.offsetZ === normalizedRight.offsetZ &&
    normalizedLeft.elevation === normalizedRight.elevation &&
    normalizedLeft.rotationDegrees === normalizedRight.rotationDegrees &&
    normalizedLeft.scale === normalizedRight.scale &&
    normalizedLeft.opacity === normalizedRight.opacity &&
    normalizedLeft.whiteThreshold === normalizedRight.whiteThreshold &&
    normalizedLeft.whiteFeather === normalizedRight.whiteFeather
}

const normalizeViewerSettings = (settings = {}) => ({
  objectIconScale: Math.max(parseFloatOr(settings.objectIconScale, 1), 0.05),
  defaultViewMode: ["3d", "2d", "walkthrough"].includes(normalizeViewMode(settings.defaultViewMode))
    ? normalizeViewMode(settings.defaultViewMode)
    : "3d",
  defaultYawDegrees: parseFloatOr(settings.defaultYawDegrees, 45),
  defaultPitchDegrees: parseFloatOr(settings.defaultPitchDegrees, 21.5),
  defaultZoomMultiplier: Math.max(parseFloatOr(settings.defaultZoomMultiplier, 1), 0.05),
  default2DZoomMultiplier: parseFloatOr(settings.default2DZoomMultiplier, 1),
  default2DOffsetX: parseFloatOr(settings.default2DOffsetX, 0),
  default2DOffsetZ: parseFloatOr(settings.default2DOffsetZ, 0),
  defaultVerticalBias: parseFloatOr(settings.defaultVerticalBias, 0),
  walkthroughOffsetX: parseFloatOr(settings.walkthroughOffsetX, 0),
  walkthroughOffsetZ: parseFloatOr(settings.walkthroughOffsetZ, 0),
  walkthroughYawDegrees: parseFloatOr(settings.walkthroughYawDegrees, 90),
  walkthroughPitchDegrees: parseFloatOr(settings.walkthroughPitchDegrees, -2),
  walkthroughZoomMultiplier: Math.max(parseFloatOr(settings.walkthroughZoomMultiplier, 1), 0.05),
  walkthroughEyeHeightRatio: Math.max(parseFloatOr(settings.walkthroughEyeHeightRatio, 0.18), 0),
  walkthroughCameraX: parseNullableFloat(settings.walkthroughCameraX),
  walkthroughCameraY: parseNullableFloat(settings.walkthroughCameraY),
  walkthroughCameraZ: parseNullableFloat(settings.walkthroughCameraZ),
  walkthroughTargetX: parseNullableFloat(settings.walkthroughTargetX),
  walkthroughTargetY: parseNullableFloat(settings.walkthroughTargetY),
  walkthroughTargetZ: parseNullableFloat(settings.walkthroughTargetZ),
  modelOffsetY: parseFloatOr(settings.modelOffsetY, 0),
  labelsVisible: parseBooleanOr(settings.labelsVisible, true),
  snapToGround: parseBooleanOr(settings.snapToGround, false)
})

export const cloneViewerSettings = (settings = {}) => normalizeViewerSettings(settings)

export const viewerSettingsEqual = (left, right) => {
  const normalizedLeft = cloneViewerSettings(left)
  const normalizedRight = cloneViewerSettings(right)

  return normalizedLeft.objectIconScale === normalizedRight.objectIconScale &&
    normalizedLeft.defaultViewMode === normalizedRight.defaultViewMode &&
    normalizedLeft.defaultYawDegrees === normalizedRight.defaultYawDegrees &&
    normalizedLeft.defaultPitchDegrees === normalizedRight.defaultPitchDegrees &&
    normalizedLeft.defaultZoomMultiplier === normalizedRight.defaultZoomMultiplier &&
    normalizedLeft.default2DZoomMultiplier === normalizedRight.default2DZoomMultiplier &&
    normalizedLeft.default2DOffsetX === normalizedRight.default2DOffsetX &&
    normalizedLeft.default2DOffsetZ === normalizedRight.default2DOffsetZ &&
    normalizedLeft.defaultVerticalBias === normalizedRight.defaultVerticalBias &&
    normalizedLeft.walkthroughOffsetX === normalizedRight.walkthroughOffsetX &&
    normalizedLeft.walkthroughOffsetZ === normalizedRight.walkthroughOffsetZ &&
    normalizedLeft.walkthroughYawDegrees === normalizedRight.walkthroughYawDegrees &&
    normalizedLeft.walkthroughPitchDegrees === normalizedRight.walkthroughPitchDegrees &&
    normalizedLeft.walkthroughZoomMultiplier === normalizedRight.walkthroughZoomMultiplier &&
    normalizedLeft.walkthroughEyeHeightRatio === normalizedRight.walkthroughEyeHeightRatio &&
    normalizedLeft.walkthroughCameraX === normalizedRight.walkthroughCameraX &&
    normalizedLeft.walkthroughCameraY === normalizedRight.walkthroughCameraY &&
    normalizedLeft.walkthroughCameraZ === normalizedRight.walkthroughCameraZ &&
    normalizedLeft.walkthroughTargetX === normalizedRight.walkthroughTargetX &&
    normalizedLeft.walkthroughTargetY === normalizedRight.walkthroughTargetY &&
    normalizedLeft.walkthroughTargetZ === normalizedRight.walkthroughTargetZ &&
    normalizedLeft.modelOffsetY === normalizedRight.modelOffsetY &&
    normalizedLeft.labelsVisible === normalizedRight.labelsVisible &&
    normalizedLeft.snapToGround === normalizedRight.snapToGround
}

export const syncLegacyViewerDatasetFromConfig = (element) => {
  const viewerConfig = viewerConfigFromDataset(element)
  const mode = viewerModeFromConfig(viewerConfig)
  const capabilities = viewerCapabilitiesFromConfig(viewerConfig)
  const assets = viewerConfig.assets || {}
  const endpoints = viewerConfig.endpoints || {}
  const viewerSettings = viewerSettingsFromPayload(viewerConfig.viewer_settings || {})

  if (Object.keys(viewerConfig).length > 0) {
    element.dataset.viewerMode = mode
    element.dataset.viewerCapabilities = JSON.stringify(capabilities)
    element.dataset.modelUrl = assets.model_url || ""
    element.dataset.modelName = assets.model_name || ""
    element.dataset.previewImageUrl = assets.preview_image_url || ""
    element.dataset.planName = viewerConfig.plan?.name || element.dataset.planName || ""
    element.dataset.referenceLayers = JSON.stringify(assets.reference_layers || [])
    element.dataset.viewerObjectIconScale = viewerSettings.objectIconScale ?? ""
    element.dataset.viewerDefaultViewMode = viewerSettings.defaultViewMode ?? ""
    element.dataset.viewerDefaultYawDegrees = viewerSettings.defaultYawDegrees ?? ""
    element.dataset.viewerDefaultPitchDegrees = viewerSettings.defaultPitchDegrees ?? ""
    element.dataset.viewerDefaultZoomMultiplier = viewerSettings.defaultZoomMultiplier ?? ""
    element.dataset.viewerDefault2dZoomMultiplier = viewerSettings.default2DZoomMultiplier ?? ""
    element.dataset.viewerDefault2dOffsetX = viewerSettings.default2DOffsetX ?? ""
    element.dataset.viewerDefault2dOffsetZ = viewerSettings.default2DOffsetZ ?? ""
    element.dataset.viewerDefaultVerticalBias = viewerSettings.defaultVerticalBias ?? ""
    element.dataset.viewerWalkthroughOffsetX = viewerSettings.walkthroughOffsetX ?? ""
    element.dataset.viewerWalkthroughOffsetZ = viewerSettings.walkthroughOffsetZ ?? ""
    element.dataset.viewerWalkthroughYawDegrees = viewerSettings.walkthroughYawDegrees ?? ""
    element.dataset.viewerWalkthroughPitchDegrees = viewerSettings.walkthroughPitchDegrees ?? ""
    element.dataset.viewerWalkthroughZoomMultiplier = viewerSettings.walkthroughZoomMultiplier ?? ""
    element.dataset.viewerWalkthroughEyeHeightRatio = viewerSettings.walkthroughEyeHeightRatio ?? ""
    element.dataset.viewerWalkthroughCameraX = viewerSettings.walkthroughCameraX ?? ""
    element.dataset.viewerWalkthroughCameraY = viewerSettings.walkthroughCameraY ?? ""
    element.dataset.viewerWalkthroughCameraZ = viewerSettings.walkthroughCameraZ ?? ""
    element.dataset.viewerWalkthroughTargetX = viewerSettings.walkthroughTargetX ?? ""
    element.dataset.viewerWalkthroughTargetY = viewerSettings.walkthroughTargetY ?? ""
    element.dataset.viewerWalkthroughTargetZ = viewerSettings.walkthroughTargetZ ?? ""
    element.dataset.viewerModelOffsetY = viewerSettings.modelOffsetY ?? ""
    element.dataset.viewerLabelsVisible = viewerSettings.labelsVisible === false ? "false" : "true"
    element.dataset.viewerSnapToGround = viewerSettings.snapToGround ? "true" : "false"
    element.dataset.mappingsUrl = endpoints.mappings_url || ""
    element.dataset.nodeMappingsUrl = endpoints.node_mappings_url || ""
    element.dataset.placementsUrl = endpoints.placements_url || ""
    element.dataset.navigationPointsUrl = endpoints.navigation_points_url || ""
    element.dataset.thingsUrl = endpoints.things_url || ""
    element.dataset.modelFileUrl = endpoints.model_file_url || ""
    element.dataset.referenceLayersUrl = endpoints.reference_layers_url || ""
    element.dataset.viewerSettingsUrl = endpoints.viewer_settings_url || ""
  }

  return { viewerConfig, mode, capabilities }
}
